<template>
  <div>
    <PageHeading
      class="mb-2"
      title="Nova Ação"
    />
    <v-container fluid class="mb-5">
      <v-form v-model="valid" ref="form">
        <!--
          Autocomplete for the item paths.
        -->
        <v-autocomplete
          v-model="item_paths"
          :items="items"
          :filter="items_filter"
          :search-input.sync="items_search"
          :rules="itemRules"
          auto-select-first
          placeholder="Selecione o(s) item(s)"
          multiple
          hide-selected
          validate-on-blur
          :return-object="true"
          @change="items_search = ''"
        >
          <template v-slot:selection="{ attrs, item, parent, selected }">
            <v-chip
              v-bind="attrs"
              :input-value="selected"
              label
              small
            >
              <span class="pr-2">{{ get_path_last(item.text) }}</span>
              <v-icon small @click="parent.selectItem(item)">x</v-icon>
            </v-chip>
          </template>

          <template v-slot:item="{ item, on, attrs}">
            <v-list-item v-on="on" v-bind="attrs" class="text-left">
              <v-chip light label small :key="item.value">
                {{ item.text }}
              </v-chip>
            </v-list-item>
          </template>
        </v-autocomplete>
        
        <v-textarea 
          placeholder="Escreva aqui..."
          label="Descrição"
          v-model="title"
          :rules="descriptionRules"
          outlined
          no-resize
        ></v-textarea>

        <v-sheet
          color="#d9d9d9"
          rounded
          class="pa-2 mb-10"
        >
          <div class="text-overline text-left">Lembrete</div>
          
          <v-checkbox
            v-model="is_periodic"
            label="Repetir"
            dense
          ></v-checkbox>

          <!-- In case the notification is periodic -->
          <v-expand-transition>
            <week-day-display
              v-show="is_periodic"
              v-model="days_select"
              :rules="days_select_rules"
              font-size="15px"
              hint="A data selecionada será a data limite de até esse evento deve se repetir."
            ></week-day-display>
          </v-expand-transition>
          <v-dialog
            ref="date_dialog"
            v-model="date_dialog"
            :return-value.sync="date_picker"
            persistent
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="date_picker"
                label="Escolha uma data"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="date_rules"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="date_picker"
              locale="pt-br"
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="date_dialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.date_dialog.save(date_picker)"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-dialog>

          <!-- Clock dialog -->
          <v-dialog
            ref="dialog"
            v-model="clock_dialog"
            :return-value.sync="time"
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="time"
                label="Horário"
                prepend-icon="mdi-clock-time-four-outline"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="time_rules"
                clearable
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="clock_dialog"
              v-model="time"
              format="24hr"
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="clock_dialog = false"
              >
                Cancelar
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dialog.save(time)"
              >
                OK
              </v-btn>
            </v-time-picker>
          </v-dialog>
        </v-sheet>
      </v-form>
      <!-- Floating Action Buttons. AKA: cancel and accept buttons -->
      <v-btn
        class="mx-2"
        fab
        fixed
        bottom
        left
        dark
        color="#802C12"
        @click="close()"
      >
        <v-icon dark>mdi-close</v-icon>
      </v-btn>
      <v-fab-transition>
        <v-btn
          class="mx-2"
          fab
          fixed
          bottom
          right
          dark
          color="#802C12"
          @click="send()"
        >
          <v-icon dark>mdi-check</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-container>
  </div>
</template>
<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import PageHeading from "@/components/PageHeading.vue";
import WeekDayDisplay from "@/components/WeekDayDisplay.vue";

import {
  G_USER_TASK_CREATE,
 } from "@/store/modules/helpers/action.type";

import {
  G_DEFAULT_GROUP,
	G_SELECTED_CONTEXT,
	G_SELECTED_GROUP,
	G_USER_FCM_TOKEN,
} from "@/store/modules/helpers/getter.type";

import tags_mixin from "@/mixins/tags.mixin";
import days_mixin from "@/mixins/days.mixin";
import { splitPath } from "@/store/helpers";
import { G_USER_MODULE } from "@/store/modules/helpers/module.type";

const normalize_str = (s) =>
  (s ?? '').toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export default Vue.extend({
  name: "CreateTask",
  components: {
    PageHeading,
    "week-day-display": WeekDayDisplay,
  },
  mixins: [tags_mixin, days_mixin],
  props: [ "suggested_title" ],
  // Non reactive data
  data: () => ({
    valid: false,
    days_select: 0,
    items_search: "",
    item_paths: [],
    date_dialog: false,
    date_picker: "",
    time: "",
    title: "",
    description: "",
    is_periodic: false,
    clock_dialog: false,
    date_rules: [
      v => !!v || "Uma data deve ser escolhida",
    ],
    time_rules: [
      v => !!v || "Um horário tem que ser escolhido.",
    ],
    titleRules: [
      v => !!v || "Descrição não pode ficar vazia.",
    ],
    descriptionRules: [], // No rules for description... yet
    itemRules: [
      v => v?.length > 0 || "Um item deve ser associado.",
      // TODO: Remove this requirement in the future.
      v => v?.length === 1 || "Não há suporte para mais de um item.",
    ],
  }),
  mounted() {
    if (this.$route.query?.path)
      this.item_paths = [ this.$route.query.path ];
    
    if (this.suggested_title)
      this.title = this.suggested_title;
  },
  watch: {
    // When the search changes, change the ordering of the items.
    items_search() {
      this.items.sort((a, b) => {
        const a_norm = normalize_str(a);
        const b_norm = normalize_str(b);
        return a_norm > b_norm ? 1 : (a_norm < b_norm ? -1 : 0);
      });
    },
  },
  computed: {
    context() {
      const group_id = this.$store.getters[G_SELECTED_GROUP];
      const ctxt_id = this.$store.getters[G_SELECTED_CONTEXT];
      return this.$store.getters[`${G_USER_MODULE}/${group_id}/${ctxt_id}/cached`];
    },
    items() {
      const selected_cat = this.$route.query?.cat;
      const selected_item = this.$route.query?.item;
      const arr = [];

      const ctxt_id = this.$store.getters[G_SELECTED_CONTEXT];

      this.context.categories.forEach(cat => {
        cat.items.forEach(item => {
          // If this item is not in the selected category and item selected by the user,
          // don't add them to the available items.
          if (selected_cat && selected_item && (selected_cat !== cat || selected_item !== item)) {
            return;
          } else if (selected_cat) {
            arr.push({
              text:  `${item.name}`,
              value: `/${ctxt_id}/${cat.id}/${item.id}`,
            });
          } else {
            arr.push({
              text:  `${cat.name}/${item.name}`,
              value: `/${ctxt_id}/${cat.id}/${item.id}`,
            });
          }
        });
      });
      return arr;
    },
    days_select_rules() {
      if (this.is_periodic) {
        return [v => v > 0 || "É necessário selecionar pelo menos um dia."];
      } else {
        return [];
      }
    },
    ...mapGetters([
      "loggedIn",
      "user",
      "user_id",
      G_DEFAULT_GROUP,
    ])
  },
  methods: {
    close() {
      this.$router.go(-1);
    },
    get_group_ref() {
      return this[G_DEFAULT_GROUP];
    },
    items_filter(item, query, text) {
      // If the user has already selected a category, make filter out other categories.
      if (this.$route.query?.cat && splitPath(item.value)[1] !== this.$route.query.cat) {
        return false;
      }
      if (item.header) return false;

      const text_norm = normalize_str(text);
      const query_norm = normalize_str(query);

      return text_norm.indexOf(query_norm) > -1;
    },
    // NOTE: Maybe its best to do some sort of transformation here...
    associations_get() {
      return this.item_paths.map(item => {
        if (typeof item === "string")
          item = this.items.find(i => i.value === item);

        if (typeof item !== "object")
          console.error("Expected item to be an object.");

        return {
          text: item.text,
          path: item.value,
        };
      });
    },
    get_days() {
      if (!this.is_periodic) return 0;
      return this.days_select;
    },
    /**
     * Returns the selected day, month and day relative to UTC timezone.
     */
    get_date() {
      let [year, month, day] = this.date_picker.split("-").map(n => parseInt(n));
      return `${year}-${month - 1}-${day}`;
    },
    get_time_as_minutes() {
      if (!this.time) return undefined;
      const [hours, minutes] = this.time.split(":").map(v => parseInt(v));
      return hours * 60 + minutes;
    },
    has_push_notification() {
      return 'Notification' in window              &&
             this.$store.getters[G_USER_FCM_TOKEN] &&
             !isIOS()                              &&
             !this.$store.state.user.force_email;
    },
    notification_payload() {
      return {
        title: this.title,
        body:  `Lembrar de ${this.title}`,
      };
    },
    email_payload() {
      return {
        subject: this.title,
        text:    `Lembrar de ${this.title}.`,
      };
    },
    async send() {
      if (!this.valid && !this.$refs.form.validate()) return;

      this.loading = true;

      // TODO: Change this to be more generic.
      const [item] = this.associations_get();
      const [_, ctxt, cat, _item] = item.path.split("/");
      const template = this.$store.getters["templates/template"](ctxt);
      const cat_template = template.categories[cat];

      const task = {
        title:         this.title,
        description:   this.description,
        tags:          this.tags_get(),
        week_days:     this.is_periodic ? this.get_days() : null,
        end_date:      this.is_periodic ? this.get_date() : null,
        due_date:     !this.is_periodic ? this.get_date() : null,
        time:          this.get_time_as_minutes() ?? null,
        associations:  this.associations_get(),
        notification:  this.notification_payload(),
        color:         cat_template.grade_color,
        is_periodic:   this.is_periodic,
        push_notify:   this.has_push_notification(),
        email:         this.email_payload(),
        email_notify: !this.has_push_notification(),
      };
      const group = this.get_group_ref();

      try {
        this.loading = true;

        await this.$store.dispatch(G_USER_TASK_CREATE, { task, group });

        this.close();
      } catch (err) {
        // TODO: Show error to the user.
        console.error(err);
      }
    },
    get_path_last(path) {
      let parts = path.split("/");
      if (parts.length > 1) 
        return `../${parts[parts.length - 1]}`;
      else 
        return parts[0];
    },
  }
});

function isIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform);
}
</script>
<style scoped lang="scss">
.item-action-no-margin {
  margin-right: 0 !important;
}
</style>
